/**
 *
 * @param {Date} date Date to compare
 * @returns {boolean}
 */
const isCurrentYear = (date) => {
  const currentYear = new Date().getFullYear()
  return date.getFullYear() === currentYear
}

const toLocaleDateString = (date, includeYear, includeDayOfWeek) => {
  if (!(date instanceof Date)) {
    return null
  }

  if (isNaN(date.getTime())) {
    return null
  }

  if (includeYear === null || includeYear === undefined) {
    includeYear = !isCurrentYear(date)
  }

  const options = {
    month: 'short',
    day: 'numeric',
  }

  if (includeYear) options.year = 'numeric'
  if (includeDayOfWeek) options.weekday = 'long'

  return date.toLocaleString('en-US', options)
}

const toLocaleTimeString = (date) => {
  if (!(date instanceof Date) || isNaN(date)) {
    return null
  }

  const options = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }

  return date.toLocaleTimeString('en-US', options)
}

const parseDate = (dateStr) => {
  if (!dateStr || isNaN(Date.parse(dateStr))) {
    return null
  }

  return new Date(Date.parse(dateStr))
}

/**
 * Used to format strings with dates. Like printf in C:
 *
 * formatStringWithDates("Arrived on %d at %t", [new Date(), new Date()]) => "Arrived on Mar 28 at 3:14 PM"
 *
 * @param {string} format
 * @param {Date[]|string[]} dates
 * @param {boolean} fancyFormat Format strings in a fancy format, if possible. "Fancy" refers to strings
 * like 'today' and 'yesterday' rather than actual dates like 'May 30'.
 * @param {Date|null} dateNow Optional datenow reference.
 * @returns {string}
 */
function formatStringWithDates(format, dates, fancyFormat = false, dateNow = null) {
  let index = 0
  if (!dateNow) {
    dateNow = new Date()
  }
  const dateYesterday = new Date(dateNow)
  dateYesterday.setDate(dateNow.getDate() - 1)

  return format.replace(/%[dt]/g, (match) => {
    let date = dates[index++]
    if (!(date instanceof Date)) {
      const parsed = parseDate(date)
      if (!parsed) {
        console.error(`Failed to parse date: ${date}`)
      }
      date = parsed ?? new Date()
    }
    if (match === '%t') {
      return toLocaleTimeString(date) ?? ''
    }
    if (match === '%d') {
      if (fancyFormat) {
        if (isSameDay(date, dateNow)) {
          return 'today'
        }
        if (isSameDay(date, dateYesterday)) {
          return 'yesterday'
        }
      }
      return toLocaleDateString(date, null, null) ?? ''
    }
    console.error(`Unrecognized format specifier: ${match}`)
    return toLocaleDateString(date, null, null) ?? ''
  })
}

/**
 *
 * @param {Date} date1
 * @param {Date} date2
 * @returns
 */
function isSameDay(date1, date2) {
  if (!date1 || !date2) {
    return false
  }

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

const getDaysUntil = (dateString) => {
  const date = new Date(dateString)
  const oneDay = 24 * 60 * 60 * 1000
  const diffDays = Math.abs((date - new Date()) / oneDay)

  return diffDays
}

export {
  getDaysUntil,
  formatStringWithDates,
  isCurrentYear,
  isSameDay,
  parseDate,
  toLocaleDateString,
  toLocaleTimeString,
}
